import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type ReactNode, useMemo, useState } from 'react';

import { type AudienceOptionType, useAudienceOptions } from '@/api/audience';
import { InfiniteComboboxPickerMultiple } from '@/components/Form/pickers';
import { type DebouncedComboboxProps } from '@/components/Form/pickers/primitives/combobox';

type Props = DebouncedComboboxProps<AudienceOptionType, true> & {
  className?: string;
  disabled?: boolean;
  notFoundContent?: ReactNode;
  placeholder?: string;
  value?: AudienceOptionType[];
};

const AudienceMultiSelect = ({
  value,
  isLoading: isLoadingProp,
  size,
  className,
  disabled = false,
  placeholder: placeholderProp,
  notFoundContent: notFoundContentProp,
  searchDebounceMs,
  onChange,
  ...rest
}: Props) => {
  const placeholder = placeholderProp ?? t`Select Audiences...`;
  const notFoundContent = notFoundContentProp ?? <Trans>No audiences were found...</Trans>;
  const [search, setSearch] = useState('');
  const { audienceOptions, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useAudienceOptions({ search });

  const audienceOptionsById = useMemo(
    () => new Map((audienceOptions ?? []).map((audience) => [audience.id, audience])),
    [audienceOptions],
  );
  const resolvedValue = useMemo(() => {
    if (!value?.length) return [];
    return value
      .map((audience) => audienceOptionsById.get(audience.id) ?? audience)
      .filter((audience) => Boolean(audience.name));
  }, [audienceOptionsById, value]);

  return (
    <InfiniteComboboxPickerMultiple<AudienceOptionType>
      {...rest}
      data-testid="audience-multiselect"
      filteredItems={audienceOptions}
      isLoading={isLoadingProp || isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasMore={hasMore}
      fetchNextPage={fetchNextPage}
      disabled={disabled}
      size={size}
      value={resolvedValue}
      onChange={onChange}
      className={className}
      placeholder={placeholder}
      notFoundContent={notFoundContent}
      onSearch={setSearch}
      searchDebounceMs={searchDebounceMs}
    />
  );
};

export default AudienceMultiSelect;
